import PaginationControls from '../components/common/PaginationControls';
import HistoryFilters from '../components/history/HistoryFilters';
import { ExecutionLogTable, RealizedTradeTable } from '../components/history/HistoryTables';
import HistoryTabs from '../components/history/HistoryTabs';
import CrossMarketSnapshot from '../components/market/CrossMarketSnapshot';
import MarketScopeTabs from '../components/market/MarketScopeTabs';
import { useCrossMarketStatus } from '../hooks/useCrossMarketStatus';
import { useTradingHistory } from '../hooks/useTradingHistory';
import './TradingHistory.css';

export type HistoryTab = 'execution' | 'realized';

export default function TradingHistory() {
  const marketStatus = useCrossMarketStatus();
  const isUsLive = isUsLivePilot(marketStatus.data);
  const state = useTradingHistory(marketStatus.data ? (isUsLive ? 'live' : 'paper') : 'live');
  const isKrExecution = state.market === 'KR' && state.tab === 'execution';
  const isKrRealized = state.market === 'KR' && state.tab === 'realized';
  const page = isKrExecution
    ? state.krDecisionPage
    : state.tab === 'execution' ? state.executionPage : state.tradePage;
  const executionItems = isKrExecution ? state.krDecisionPage.items : state.executionPage.items;
  const hasRows = page.items.length > 0;

  return (
    <div className="trading-history-page">
      <header className="page-header">
        <h1>매매 이력</h1>
        <p>US 운영 로그와 KR replay 판단 이력을 시장별로 분리해서 확인합니다.</p>
      </header>

      <HistoryControls state={state} marketStatus={marketStatus} />
      <HistoryScopeNote state={state} isUsLive={isUsLive} statusLoading={marketStatus.loading} />
      <HistoryBody
        state={state}
        page={page}
        hasRows={hasRows}
        isKrExecution={isKrExecution}
        isKrRealized={isKrRealized}
        executionItems={executionItems}
        marketStatus={marketStatus}
      />
    </div>
  );
}

function HistoryControls({ state, marketStatus }: HistorySectionProps) {
  return (
    <>
      <MarketScopeTabs value={state.market} onChange={state.changeMarket}
        status={marketStatus.data} includeAll={false} />
      {state.market !== 'US' && (
        <CrossMarketSnapshot status={marketStatus.data} scope={state.market}
          loading={marketStatus.loading} error={marketStatus.error} />
      )}
      <HistoryTabs tab={state.tab} onTabChange={state.changeTab} />
      <HistoryFilters tab={state.tab} market={state.market}
        symbol={state.symbol} action={state.action} tradeType={state.tradeType}
        startDate={state.startDate} endDate={state.endDate}
        onSymbolChange={state.setSymbol} onActionChange={state.setAction}
        onTradeTypeChange={state.setTradeType}
        onStartDateChange={state.setStartDate}
        onEndDateChange={state.setEndDate} onSearch={() => state.setOffset(0)} />
    </>
  );
}

function HistoryScopeNote({ state, isUsLive, statusLoading }: {
  state: HistoryState; isUsLive: boolean; statusLoading: boolean;
}) {
  if (state.market === 'KR') {
    const text = state.tab === 'realized'
      ? 'KR은 replay 검증 단계라 실현 손익을 만들지 않습니다.'
      : 'KR 탭은 replay 판단 이력만 표시하며 실제 주문/체결 이력이 아닙니다.';
    return <section className="history-scope-note"><strong>KR Replay</strong><span>{text}</span></section>;
  }
  if (state.tab === 'realized') {
    const text = statusLoading
      ? '운영 상태 확인 중입니다. 확인 전에는 과거 paper 손익을 현재 live 성과로 표시하지 않습니다.'
      : isUsLive
        ? 'US live_pilot 실현 손익만 조회합니다. 첫 live 매도 체결 전까지 데이터 없음이 정상입니다.'
        : 'US paper/기존 실현 손익 기록을 조회합니다.';
    return <section className="history-scope-note"><strong>US 실현 손익</strong><span>{text}</span></section>;
  }
  return (
    <section className="history-scope-note">
      <strong>US 전략 실행 로그</strong>
      <span>HOLD와 주문 미실행 사유를 포함한 전략 실행 기록입니다. 실현 성과는 실현 손익 탭에서 따로 확인합니다.</span>
    </section>
  );
}

function HistoryBody(props: HistoryBodyProps) {
  if (props.isKrRealized) return <KrHistoryEmpty status={props.marketStatus.data} />;
  if (props.state.loading) return <div className="state-box">로딩 중...</div>;
  if (props.state.error) return <div className="state-box error-state">API 오류: {props.state.error}</div>;
  if (!props.hasRows) return <div className="state-box">데이터 없음</div>;
  return (
    <>
      {props.state.tab === 'execution'
        ? <ExecutionLogTable logs={props.executionItems} marketLabel={props.isKrExecution ? 'KR Replay' : 'US'} />
        : <RealizedTradeTable trades={props.state.tradePage.items} />}
      <PaginationControls {...props.page} onPageChange={props.state.setOffset} />
    </>
  );
}

type HistoryState = ReturnType<typeof useTradingHistory>;
type MarketStatus = ReturnType<typeof useCrossMarketStatus>;
type HistorySectionProps = { state: HistoryState; marketStatus: MarketStatus };
type HistoryBodyProps = HistorySectionProps & {
  page: { total: number; limit: number; offset: number };
  hasRows: boolean;
  isKrExecution: boolean;
  isKrRealized: boolean;
  executionItems: Parameters<typeof ExecutionLogTable>[0]['logs'];
};

function KrHistoryEmpty({ status }: { status: ReturnType<typeof useCrossMarketStatus>['data'] }) {
  const kr = status?.markets.KR;
  return (
    <section className="history-kr-empty state-box">
      <strong>KR 실현 손익 데이터 없음</strong>
      <p>
        현재 KR 경로는 replay 검증 단계입니다.
        {kr ? ` Run ${kr.run_count}회, 판단 ${kr.decisions}건, 모의 체결 ${kr.fills}건만 존재합니다.` : ''}
      </p>
    </section>
  );
}

function isUsLivePilot(status: ReturnType<typeof useCrossMarketStatus>['data']) {
  const safety = status?.safety;
  return Boolean(
    safety?.trading_mode === 'live'
    && safety.live_trading_enabled
    && safety.toss_live_market === 'US'
    && safety.toss_us_live_enabled
  );
}
